import axios from "axios";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ExcelColumnConfig } from "fe-shared/src/types/common";

import { priceToFormat, formatWeight } from "../helpers/invoice";
import {
  formatRecord,
  formatDate,
  formatInvoiceNumber,
} from "../helpers/format";

import { DataGridRequestParams, GridInvoiceExport, NoteRecord } from "../types";

interface HistoryLogsResponse {
  [key: string]: NoteRecord[];
}

export const getInvoicesExport = async (
  requestParams: DataGridRequestParams,
  companyId?: number
) => {
  const invoices = await axios
    .post<GridInvoiceExport[]>("invoices/export", requestParams, {
      params: { companyId },
    })
    .then((res) => res.data);
  const invoiceNumbers = invoices.map((i) => i.invoiceNumber);

  if (!companyId) {
    const historyLogsByInvoiceNumber = await axios
      .post<HistoryLogsResponse>("history/fetchForExport", {
        objectIds: invoiceNumbers,
        entityType: "invoice",
      })
      .then((res) => res.data);

    return invoices.map((i) => ({
      ...i,
      historyLogs: historyLogsByInvoiceNumber[i.invoiceNumber],
    }));
  }

  return invoices;
};

const useInvoicesExport = (
  requestParams: DataGridRequestParams,
  companyId?: number
) => {
  const { t } = useTranslation();
  const isStaff = !companyId;

  const getDataRows = () => getInvoicesExport(requestParams, companyId);

  const columnConfig = useMemo(() => {
    const columns: ExcelColumnConfig<GridInvoiceExport>[] = [
      {
        width: 15,
        mappedKey: t("invoices.grid.export.cols.submittedDate"),
        getOriginalValue: (i) => formatDate(i.submittedDate),
      },
      ...(isStaff
        ? [
            {
              width: 18,
              mappedKey: t("invoices.grid.export.cols.companyRegNumber"),
              getOriginalValue: (i: GridInvoiceExport) => i.company?.regNumber,
            },
            {
              width: 40,
              mappedKey: t("invoices.grid.export.cols.companyName"),
              getOriginalValue: (i: GridInvoiceExport) =>
                i.company?.displayName,
            },
          ]
        : []),
      {
        width: 20,
        mappedKey: t("invoices.grid.export.cols.invoiceNumber"),
        getOriginalValue: (i) => formatInvoiceNumber(i.invoiceNumber),
      },
      {
        width: 20,
        mappedKey: t("invoices.grid.export.cols.invoiceTarget"),
        getOriginalValue: (i) => t(`invoices.type.${i.invoiceTarget}`),
      },
      {
        width: 30,
        mappedKey: t("invoices.grid.export.cols.period"),
        getOriginalValue: (i) =>
          `${formatDate(i.periodStartDate)} - ${formatDate(i.periodEndDate)}`,
      },
      {
        width: 20,
        mappedKey: t("invoices.grid.export.cols.sum", { currency: "$" }),
        getOriginalValue: (i) => priceToFormat(i.sum),
      },
      {
        width: 20,
        mappedKey: t("invoices.grid.export.cols.weight"),
        getOriginalValue: (i) => formatWeight(i.weight),
      },
      {
        width: 15,
        mappedKey: t("invoices.grid.export.cols.statusCode"),
        getOriginalValue: (i) => t(`invoices.statuses.${i.statusCode}`),
      },
      ...(isStaff
        ? [
            {
              width: 20,
              mappedKey: t("invoices.grid.export.cols.assignee"),
              getOriginalValue: (i: GridInvoiceExport) =>
                i.assignee?.displayName,
            },
            {
              width: 40,
              mappedKey: t("invoices.grid.export.cols.notes"),
              getOriginalValue: (i: GridInvoiceExport) =>
                (i.notes || []).map(formatRecord).join("\n"),
            },
            {
              width: 40,
              mappedKey: t("invoices.grid.export.cols.historyLogs"),
              getOriginalValue: (i: GridInvoiceExport) =>
                (i.historyLogs || []).map(formatRecord).join("\n"),
            },
          ]
        : []),
    ];

    return columns;
  }, [t, isStaff]);

  return {
    getDataRows,
    columnConfig,
  };
};

export default useInvoicesExport;
